import React, { useCallback, useState } from "react";
import { endpoints, formatErrors } from "utils/api/endpoints";
import { Label } from "app/components/ui/label";
import { Input } from "app/components/ui/input";
import { Button } from "app/components/ui/button";
import { Icons } from "app/components/ui/icons";
import { toast } from "app/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "app/components/ui/dialog";
import { isDevAccessGateEnabled } from "utils/dev-access-gate-config";

const DEV_ACCESS_STORAGE_KEY = "vle_dev_access_verified";

function readStoredVerified(): boolean {
  try {
    return localStorage.getItem(DEV_ACCESS_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function persistVerified(): void {
  try {
    localStorage.setItem(DEV_ACCESS_STORAGE_KEY, "1");
  } catch {
    // ignore quota / private mode
  }
}

type VerifyResult =
  | { ok: true; message?: string }
  | { ok: false; kind: "invalid_password" | "api_error"; message: string };

async function verifyDevPassword(password: string): Promise<VerifyResult> {
  const url = `${endpoints.baseUrl}${endpoints.verifyDev.url}`;
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ password }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;

    if (res.ok) {
      const inner = data?.data as { message?: string } | undefined;
      const msg =
        typeof inner?.message === "string" ? inner.message : undefined;
      return { ok: true, message: msg };
    }

    const formatted = formatErrors(data);
    const errMsg =
      formatted !== "Something went wrong"
        ? formatted
        : (typeof data?.message === "string" ? data.message : null) ||
          res.statusText ||
          "Verification failed";

    if (
      res.status === 400 ||
      res.status === 401 ||
      res.status === 403 ||
      res.status === 422
    ) {
      return { ok: false, kind: "invalid_password", message: errMsg };
    }

    return { ok: false, kind: "api_error", message: errMsg };
  } catch {
    return {
      ok: false,
      kind: "api_error",
      message: "Network error. Please try again.",
    };
  }
}

type DevAccessGateDialogProps = {
  children: React.ReactNode;
};

export function DevAccessGateDialog({ children }: DevAccessGateDialogProps) {
  const [verified, setVerified] = useState(() => {
    if (!isDevAccessGateEnabled()) return true;
    return readStoredVerified();
  });
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback(async () => {
    const trimmed = password.trim();
    if (!trimmed || isLoading) return;

    setIsLoading(true);
    try {
      const result = await verifyDevPassword(trimmed);
      if (result.ok) {
        persistVerified();
        setVerified(true);
        toast({
          title: "Access granted",
          description:
            result.message || "Dev environment verified successfully.",
          variant: "success",
        });
        setPassword("");
        return;
      }

      if (result.kind === "invalid_password") {
        toast({
          title: "Incorrect password",
          description: result.message,
          variant: "error",
        });
        return;
      }

      toast({
        title: "Verification failed",
        description: result.message,
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [password, isLoading]);

  if (!isDevAccessGateEnabled()) {
    return <>{children}</>;
  }

  if (verified) {
    return <>{children}</>;
  }

  return (
    <Dialog open>
      <DialogContent
        overlayClassName="before:hidden bg-[#FE4D01]/5 backdrop-blur-sm"
        className="top-1/2 bg-white w-[95%] sm:max-w-[33.5rem] p-4 sm:p-5 lg:p-6 gap-3.5 sm:gap-4 lg:gap-5"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex-row justify-between items-center p-0 bg-transparent">
          <DialogTitle className="text-xl font-bold leading-5 text-black sm:leading-6 md:leading-7 lg:text-2xl font-mobile">
            Developer access
          </DialogTitle>
        </DialogHeader>
        <div className="">
          <Label
            htmlFor="dev-access-password"
            className="w-fit text-black text-xs md:text-sm font-medium leading-4 md:leading-5 -mt-2 sm:mt-0 mb-1.5 sm:mb-2 md:mb-[0.7rem]"
          >
            Development password
          </Label>
          <div className="relative">
            <Input
              id="dev-access-password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              disabled={isLoading}
              className="w-full h-[2.625rem] md:h-[2.875rem] p-2 pr-10 md:p-[0.78125rem] md:pr-11 md:pb-[0.84375rem] border rounded-xl text-black text-sm font-medium leading-4 md:leading-5 focus-visible:outline focus:outline-[#707C8F] border-[#D1D5DB] focus-visible:outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleSubmit();
                }
              }}
              maxLength={20}
              placeholder="Enter password"
            />
            {password.length > 0 && (
              <div className="absolute top-1/2 -translate-y-1/2 right-3 md:right-[0.875rem] w-4 md:w-[1.125rem] h-4 md:h-[1.125rem] cursor-pointer">
                <img
                  src={
                    showPassword
                      ? "/assets/eye-close.svg"
                      : "/assets/eye-open.svg"
                  }
                  className="w-full h-auto"
                  onClick={() => setShowPassword(!showPassword)}
                  alt={showPassword ? "Hide" : "Show"}
                />
              </div>
            )}
          </div>
        </div>
        <Button
          type="button"
          variant="primary"
          disabled={isLoading || !password.trim()}
          onClick={() => void handleSubmit()}
        >
          {isLoading ? (
            <div className="flex items-center gap-0.5 sm:gap-1">
              <Icons.Spinner className="w-4 h-4 animate-spin md:w-5 md:h-5" />
              <span>Verifying...</span>
            </div>
          ) : (
            "Continue"
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
